import type { MessageKey, Translate } from '../i18n'
import { BffError } from './errors'

/**
 * A server error, in the reader's language.
 *
 * The services answer with an error ENVELOPE — a stable machine `code` and a
 * human `message` — and the SPA was rendering the message. That message is
 * written in English by a Go service and a Node BFF, so a French user met
 * "authentication required" on a French screen (MPAY-32206).
 *
 * The code is the part worth translating: it is the contract, it is stable,
 * and it is what the two services agree on. The message is the fallback, which
 * is not a shrug — those strings are already written to be read by a user
 * ("your account is not assigned to a biller"), so an untranslated one is
 * merely English rather than a stack trace. That property is what makes a
 * partial map honest: the codes a biller actually meets are here, the
 * operational ones are not, and nothing has to be exhaustive to be correct.
 *
 * Deliberately NOT keyed on HTTP status: 400 covers a dozen unrelated
 * situations and telling a user "bad request" says nothing they can act on.
 */
const MESSAGES: Readonly<Record<string, MessageKey>> = {
  // The session is gone. Far and away the most common one a user will see,
  // and the one QA reported.
  unauthenticated: 'apiError.sessionEnded',
  session_expired: 'apiError.sessionEnded',
  // The session changed underneath this tab — a second tab signed in as
  // somebody else, most often.
  csrf_failed: 'apiError.sessionChanged',
  forbidden: 'apiError.notPermitted',
  // The organisation itself is shut, which no retry and no other account of
  // theirs can change (FR-11).
  organisation_disabled: 'apiError.organisationDisabled',
  unauthorized: 'apiError.notPermitted',
  not_found: 'apiError.notFound',
  rate_limited: 'apiError.tooMany',
  export_too_large: 'apiError.exportTooLarge',
  // A dependency is down. Distinct from a bug: the user should try again
  // rather than change what they asked for.
  warehouse_unavailable: 'apiError.temporarilyUnavailable',
  registry_unavailable: 'apiError.temporarilyUnavailable',
  policy_unavailable: 'apiError.temporarilyUnavailable',
  upstream_unavailable: 'apiError.temporarilyUnavailable',
  identity_provider_unavailable: 'apiError.temporarilyUnavailable',
  support_unavailable: 'apiError.temporarilyUnavailable',
  internal_error: 'apiError.unexpected',
}

/**
 * Returns the message to show for a failed request, and the request id when
 * one came back — the id is what makes a support call actionable, so it is
 * kept separate rather than glued into the sentence by every caller.
 */
export function apiErrorText(t: Translate, error: unknown): { text: string; requestId: string | null } {
  if (error instanceof BffError) {
    const key = MESSAGES[error.code]
    return { text: key ? t(key) : error.message, requestId: error.requestId }
  }
  if (error instanceof Error) return { text: error.message, requestId: null }
  return { text: t('error.title'), requestId: null }
}
